import React, { FC } from 'react';
import styled from '@emotion/styled';

import { FontSecondarySemiBold } from '../../../styles/fonts';
import { Icon } from '../Icons/Icon';
import { ProductFees, Tooltip, TooltipList, TooltipListItem } from '@digando/common-ui';
import { sortPricesByDays } from '../../../helper/prices-helper';
import { readablePrice } from '../../../helper/readable-price';
import { spacingBottom, spacingRight, spacingTop } from '../../../helper/spacing';
import { mediaQueries } from '../../../styles/media-queries';
import styles from './product-price.module.scss';
import {
  PriceService_PriceAmountTypeEnum,
  ProductSearchItemPreviewPriceInformationFragment
} from '../../../@types/codegen/graphql';
import { useTranslation } from 'next-i18next';
import { useSession } from 'next-auth/react';
import { ServiceFeeTitle } from '../../../helper/service-fee-title-by-key';

export type PriceInformationDomain = {
  workingDays: number,
  productPricePerDay: { amount: number, type: 'EQ' | 'MIN' };
  productPriceReducedPerDay: { amount: number, type: 'EQ' | 'MIN' };
  tierPrices: { minDays: number, price: number }[];
  serviceFees: { key: string, value: number }[];
};

export interface IProductPriceProps {
  productPrice: PriceInformationDomain;
}

export const toDynamicPriceDomain = (priceInformation: ProductSearchItemPreviewPriceInformationFragment): PriceInformationDomain => {
  return {
    workingDays: 0,
    tierPrices: priceInformation.tierPrices,
    productPricePerDay: {
      amount: priceInformation.productPricePerDay.amount,
      type: priceInformation.productPricePerDay.type,
    },
    productPriceReducedPerDay: {
      amount: priceInformation.productPricePerDay.amount,
      type: priceInformation.productPricePerDay.type,
    },
    serviceFees: priceInformation.serviceFees,
  }
}

/**
 * Will display the product price in the product card.
 * - For unauthenticated users, the price will be displayed as "from 9,99€ / day".
 * - For authenticated users, the exact price will be displayed.
 *   - For reduced prices, the old price will be stricken out.
 */
export const ProductPrice: FC<IProductPriceProps> = ({ productPrice }) => {
  const [t] = useTranslation(['search', 'checkout-page']);
  const { status } = useSession();

  const tierPrices = sortPricesByDays(productPrice.tierPrices.map(price => ({ amount: price.price, minDays: price.minDays })));

  const generalPrice = tierPrices[tierPrices.length - 1];

  const fromPrice = <>{t('search:from-label')} {readablePrice(generalPrice.amount)} / {t('search:day')}</>;
  const detailedPrice = <>
    {(productPrice?.productPricePerDay.amount ?? 0) === (productPrice?.productPriceReducedPerDay.amount ?? 0)
      ? <>
        {PriceService_PriceAmountTypeEnum.Min === productPrice?.productPriceReducedPerDay.type && (<>ab</>)} {readablePrice(productPrice?.productPriceReducedPerDay.amount ?? 0)} / {t('search:day')}
      </>
      : (<>
        <OldPrice>{t('search:reduced-price-pre-fix')} {readablePrice(productPrice?.productPricePerDay.amount ?? 0)} / {t('search:day')}</OldPrice>
        {readablePrice(productPrice?.productPriceReducedPerDay.amount ?? 0)} / {t('search:day')}
      </>)
    }
  </>;

  const items: TooltipListItem[] = tierPrices.map(price => {
    return {
      key: t('search:day-label', { count: price.minDays }),
      value: readablePrice(price.amount),
    };
  });

  return (
    <div className={styles.productPrice}>
      <div className={styles.priceInfoColumn}>
        <Tooltip tooltipContent={<TooltipList items={items} />}>
          <div className={styles.priceInfoButton}>
            <span className={styles.detailsLabel}>{t('search:price-details-label')}</span>
            <Icon icon={'info-outline'} size={'17px'}/>
          </div>
        </Tooltip>
      </div>

      <GeneralPriceColumn>
        {status !== 'authenticated' ? fromPrice : detailedPrice}
      </GeneralPriceColumn>
      <ProductFees
        serviceFees={productPrice.serviceFees.map(serviceFee => {
          return {
            key: serviceFee.key,
            value: serviceFee.value,
            title: <ServiceFeeTitle feeType={serviceFee.key} />,
          };
        })}
        translations={{
          serviceFeeTitle: t('checkout-page:product-price.service-fee-title'),
        }}
      />
    </div>
  );
};

const GeneralPriceColumn = styled.div`
  ${FontSecondarySemiBold};
  ${spacingRight(0.5)};
  ${spacingTop(1)};
  ${FontSecondarySemiBold};
  font-size: var(--font-size-headline-h4);
  line-height: 24px;
  color: var(--black);
  white-space: nowrap;
  ${spacingRight(0.5)};

  @media ${mediaQueries.laptopL} {
    ${spacingRight(0)};
    ${spacingBottom(1.5)};

    &:last-of-type {
      ${spacingRight(0.5)};
    }
  }
`;

const OldPrice = styled.div`
  ${FontSecondarySemiBold};
  font-size: var(--font-size-text-small);
  line-height: 14px;
  text-align: right;
  text-decoration: line-through;
`;
